import { getModuleSettings } from "../requests/settings.request";
import {
  Area,
  CustomContactFormFields,
  GuestsCapacity,
  ModuleSettings,
  ExternalPartner,
  WaitingMessage,
  Translation,
  PeriodsTranslations,
  PeriodTranslations,
  UpdateFromBooking,
  ErrorMessage,
  SuggestRestaurant,
} from "../types";
import { Observable } from "rxjs";
import { tap } from "rxjs/operators";
import { selectionStore } from "./selection.store";
import { requestParamsImpl } from "../services/request-params.service";
import { getLocalStorageService } from "../services/local-storage.service";
import selectionGuestsService from "../services/selection-guests-service";
import { pendingBookingStore } from "./pending-booking.store";
import moment, { Moment } from "moment-timezone";
import facebookPixelService from "../services/facebook-pixel.service";
import { getLanguageSwitcherService } from "../services/language-switcher.service";
import selectionCalendarDateService from "../services/selection-calendar-date.service";
import i18n from "../i18n";
import { config } from "../config";

class ModuleSettingsStore {
  public settings: ModuleSettings = {} as ModuleSettings;
  public initialBookingCreated: boolean = false;
  public moduleInitialized: boolean = false;

  public getAvailableLocales(): Array<string> {
    return this.settings.available_locales;
  }

  public getDefaultLocale(): string {
    return this.settings.restaurant?.locale ?? "en";
  }

  public getRestaurantName(): string {
    return this.settings.restaurant?.name ?? "";
  }

  public getRestaurantId(): string {
    return String(this.settings.restaurant?.id);
  }
  //!\TODO on update_from email referal_key is not setted
  //!\ hence if moduleSettings.settings == {}
  //!\ moduleSetting.getReferralKey may since settings.referak_key is undefined
  public getReferralKey(): string {
    return this.settings.referral_key;
  }

  public getCapacity(): GuestsCapacity {
    return {
      minGuests: this.settings.restaurant.min_persons ?? 0,
      maxGuests: this.settings.restaurant.max_persons ?? 12,
    };
  }
  public get monitoringUserId(): string {
    return `${moduleSettings.getReferralKey()}_${moduleSettings.getRestaurantId()}`;
  }

  public get disclaimerUrl(): string {
    return this.settings.terms?.disclaimer_url ?? "";
  }

  public get prepaymentDisclaimerUrl(): string {
    return this.settings.terms?.prepayment_disclaimer_url ?? "";
  }

  public get privacyPolicyUrl(): string {
    return this.settings.terms?.privacy_policy_url ?? "";
  }

  public get customFields(): CustomContactFormFields {
    var customFields = this.settings as CustomContactFormFields;
    ["birthday", "gender", "company", "address"].forEach((field) => {
      const customFieldsKey = "show_" + field;
      customFields[customFieldsKey as keyof CustomContactFormFields] =
        this.settings.optional_contact_form_fields?.includes(field);
    });
    return customFields;
  }

  public get partnerName(): string {
    return this.settings.partner_display_name;
  }

  public get productName(): string {
    return this.settings.product_name;
  }

  public get requiredContactFields(): string[] {
    let defaultRequiredFields = [
      "lastname",
      "firstname",
      "mobile_phone",
      "terms",
    ];
    if (!this.settings.allow_blank_email) defaultRequiredFields.push("email");

    return defaultRequiredFields;
  }

  public get multiVouchersEnabled(): boolean {
    return this.settings.restaurant.allow_multi_vouchers;
  }

  public get onlineBookingEnabled(): boolean {
    return this.settings.restaurant?.online_booking_enabled;
  }

  public get restaurantLogoUrl(): string {
    return this.settings.banner_url;
  }

  public get backgroundImageUrl(): any {
    return this.settings.background_picture_url;
  }

  public get bodyBackgroundColor(): any {
    return this.settings.theme?.bodyBackgroundColor;
  }

  public get fontFamily(): any {
    return this.settings.google_font || "Roboto";
  }

  public get restaurantTimezone(): string {
    return this.settings.restaurant?.time_zone ?? "Europe/Paris";
  }

  public get areas(): Array<Area> {
    return this.settings.restaurant!.areas;
  }

  public get areaIds(): Array<number> {
    return this.settings.restaurant!.areas.map((area) => area.id);
  }

  public get forceAreaSelection(): boolean {
    return (
      (this.settings.advanced_options?.force_area_selection &&
        this.areaIds.length > 1) ??
      false
    );
  }

  public get hideServiceSelection(): boolean {
    return this.settings.advanced_options?.hide_filter_selection ?? false;
  }

  get hasSkippingGroupPpt(): boolean {
    return (
      this.settings.advanced_options?.has_group_skipping_prepayment ?? false
    );
  }

  get isRecaptchaEnabled(): boolean {
    return this.settings.advanced_options?.recaptcha_enabled ?? false;
  }

  get enableRestaurantSuggestions(): boolean {
    const options = this.settings.advanced_options;
    return Boolean(
      options?.enable_restaurant_suggestions &&
        options?.restaurants_to_suggest?.length
    );
  }

  get restaurantToSuggest(): Array<SuggestRestaurant> {
    return this.settings.advanced_options?.restaurants_to_suggest ?? [];
  }

  public get periods(): Array<string> {
    if (typeof this.settings.restaurant!.periods[0] === "object") {
      const _periods: Array<PeriodsTranslations> =
        this.settings.restaurant!.periods;
      return _periods.map(({ period }) => period);
    }
    return this.settings.restaurant!.periods;
  }

  public get last_booking_date(): string {
    return this.settings.restaurant!.last_booking_date;
  }

  public get getCustomTheme(): any {
    return this.settings.theme;
  }

  public get primaryColor(): any {
    return this.settings.theme.primaryColor;
  }

  public get callFromFloatingButton(): any {
    return this.settings.callFromFloatingButton;
  }

  public get selectOnlyPaxFirst(): boolean {
    return this.settings.advanced_options?.select_only_pax_first ?? false;
  }

  public get defaultPersons(): number {
    return this.settings.restaurant.default_persons ?? 0;
  }

  public get settingsAreDefined(): boolean {
    return Object.keys(this.settings).length !== 0;
  }

  public get reservationResponsableEnabled(): boolean {
    return (
      this.settings.advanced_options?.reservation_responsable_enabled ?? false
    );
  }

  public get delayBeforeDepositCapture(): number {
    return this.settings.restaurant?.delay_before_deposit_capture ?? 0;
  }

  public get diplayAllergiesField(): boolean {
    return this.settings.restaurant?.display_allergies_field ?? false;
  }

  public get externalPartner(): ExternalPartner | undefined {
    return this.settings.external_partner;
  }

  public get poweredByLogo(): string {
    return this.settings.powered_by.logo;
  }

  public get poweredByUrl(): string {
    return this.settings.powered_by.url;
  }

  public get childrenManagementEnabled(): boolean {
    return (
      this.settings.restaurant?.children?.children_management_enabled ?? false
    );
  }

  public get childrenMaxAge(): number {
    return this.settings.restaurant?.children?.children_max_age ?? 12;
  }

  public headerMessage(lang: string, location: string): string | null {
    const _locale = getLanguageSwitcherService().golLocaleFromSwitcher(lang);
    if (location.endsWith("selection")) {
      return this.getGeneralInfoMessage(_locale);
    }
    return pendingBookingStore.getPendingBookingMessage(_locale);
  }

  public waitingMessage(locale: string): string | null {
    if (pendingBookingStore.isWaitingState()) {
      return this.computeWaitingMessage(locale);
    }
    return null;
  }

  public loadWaitingMessageForLocale(
    locale: string
  ): WaitingMessage | undefined {
    let _locale = getLanguageSwitcherService().golLocaleFromSwitcher(locale);
    const { waiting_messages } = this.settings;
    return waiting_messages && waiting_messages.translations
      ? waiting_messages.translations.find(
          (waitingMessage: WaitingMessage) => waitingMessage.locale === _locale
        )
      : undefined;
  }

  public computeWaitingMessage(locale: string): string | null {
    const waitingMessage: WaitingMessage | undefined =
      this.loadWaitingMessageForLocale(locale);
    let message: string | null = null;
    if (waitingMessage) {
      message =
        waitingMessage.restaurant_message ?? waitingMessage.default_message;
    }
    return message;
  }

  public getGeneralInfoMessage(lang: string): string | null {
    const note =
      this.settings.restaurant.bookings_detail?.encoded_note ??
      this.settings.restaurant.bookings_detail?.note ??
      null;
    if (note) {
      const _note =
        note[lang] || note[this.settings.restaurant.locale] || note["en"];
      return decodeURIComponent(_note);
    }
    return null;
  }

  public getPoweredByText(locale: string): string | undefined {
    const poweredByTexts = this.settings.powered_by.powered_by_texts;
    const locales = poweredByTexts.translations.map(
      (translation) => translation.locale
    );
    const _locale = locales.includes(locale) ? locale : "en";
    return poweredByTexts && poweredByTexts.translations
      ? poweredByTexts.translations.find(
          (poweredByText: Translation) => poweredByText.locale === _locale
        )?.text
      : undefined;
  }

  get bookingDate(): Moment | undefined {
    return this.isUpdateFromBooking &&
      !this.isUpdateFromBookingError &&
      this.settings.update_from_booking.booking_date
      ? moment(this.settings.update_from_booking.booking_date)
      : undefined;
  }

  get bookingTime(): string | undefined {
    return this.isUpdateFromBooking &&
      !this.isUpdateFromBookingError &&
      this.settings.update_from_booking.booking_time
      ? moment(this.settings.update_from_booking.booking_time)
          .tz(this.restaurantTimezone)
          .format("HH") +
          ":" +
          moment(this.settings.update_from_booking.booking_time).format("mm")
      : undefined;
  }

  get updateFromBooking(): UpdateFromBooking {
    return this.settings.update_from_booking;
  }

  get childrenNumber(): number | undefined {
    return this.isUpdateFromBooking &&
      !this.isUpdateFromBookingError &&
      this.settings.update_from_booking.children_number
      ? this.settings.update_from_booking.children_number
      : undefined;
  }

  get areaId(): string | null {
    return this.isUpdateFromBooking &&
      !this.isUpdateFromBookingError &&
      this.settings.update_from_booking.area_id
      ? this.settings.update_from_booking.area_id
      : null;
  }

  get isUpdateFromBooking(): boolean {
    if (!this.settings.update_from_booking) {
      return false;
    }
    const { booked_persons_number, booking_date, booking_time } =
      this.settings.update_from_booking;
    return (
      booked_persons_number !== null &&
      booking_date !== null &&
      booking_time !== null
    );
  }

  get isUpdateFromBookingError(): boolean {
    return (
      this.isUpdateFromBooking &&
      this.settings.update_from_booking.error_message[0] !== null
    );
  }

  get isPreCatelanRestaurant(): boolean {
    return this.isFrPlt
      ? [7894, 8580].includes(this.settings.restaurant?.id)
      : false;
  }

  get isTourArgentGroupRestaurant(): boolean {
    return this.isFrPlt
      ? [1997, 8338, 8339, 1469].includes(this.settings.restaurant?.id)
      : false;
  }

  get isLeCastelletGroupRestaurant(): boolean {
    return this.isFrPlt
      ? [8184, 8185, 8572].includes(this.settings.restaurant?.id)
      : false;
  }

  get isFrPlt(): boolean {
    return config.appPlatform?.slice(config.appPlatform?.length - 2) === "fr";
  }

  get isRoPlt(): boolean {
    return config.appPlatform?.slice(config.appPlatform?.length - 2) === "ro";
  }

  get customLabelSmsSpamRestaurantGroup(): string | null {
    if (this.isTourArgentGroupRestaurant) {
      return "Tour d’Argent";
    }
    return null;
  }

  get shouldHideLegend(): boolean {
    return (
      process.env.REACT_APP_PLATFORM?.slice(
        process.env.REACT_APP_PLATFORM?.length - 2
      ) === "fi" && [1444, 1470].includes(this.settings.restaurant?.id)
    );
  }

  get redirectionLink(): string | null {
    return this.settings.redirection_link;
  }

  public computeUpdateFromBookingErrorMessage(
    locale: string
  ): string | undefined {
    const _locale = getLanguageSwitcherService().golLocaleFromSwitcher(locale);
    const errorMessages = this.settings.update_from_booking.error_message;
    return errorMessages
      ? errorMessages.find(
          (errorMessage: ErrorMessage) => errorMessage.locale === _locale
        )?.error_message
      : undefined;
  }

  public get restaurantCountryCode(): string {
    return this.settings.restaurant?.country_code ?? "en";
  }

  public hasFacebookPixel(): boolean {
    return !!this.externalPartner?.facebook_pixel_id;
  }

  public renamingPeriod(period: string): string {
    let _locale = getLanguageSwitcherService().golLocaleFromSwitcher(
      i18n.language
    );
    let renamingPeriod: string;
    if (typeof this.settings.restaurant.periods[0] === "string") {
      let _periods: PeriodsTranslations[] = [];
      if (this.settings.restaurant.periods_translations !== undefined) {
        _periods = this.settings.restaurant.periods_translations.filter(
          (periodsTranslations: PeriodsTranslations) =>
            Object.keys(periodsTranslations).toLocaleString() === period
        );
      }
      renamingPeriod =
        _periods.length > 0
          ? Object.values(_periods[0])[0][_locale]
          : undefined;
    } else {
      const _period = this.settings.restaurant.periods.find(
        (periods: PeriodsTranslations) => periods.period === period
      );
      renamingPeriod = _period
        ? _period.translations.find(
            (translation: PeriodTranslations) => translation.locale === _locale
          )?.text
        : undefined;
    }
    return renamingPeriod
      ? renamingPeriod
      : i18n.t("instabook.periods." + period);
  }

  //Initialize store from given ModuleSettings
  // keep arrow function to handle this when called from initModuleSettings
  public initFromModuleSettings = (response: ModuleSettings) => {
    this.settings = response;
    requestParamsImpl.restaurantId = String(this.settings.restaurant.id);
    requestParamsImpl.referralKey = this.settings.referral_key;
    if (!moduleSettings.moduleInitialized) {
      getLocalStorageService().setDefaultLocalLanguage();
      selectionCalendarDateService.setDefaultDate();
    }
    selectionGuestsService.setDefaultSelectedGuest();
    selectionStore.setDefaultAreaId();
    selectionCalendarDateService.setDefaultTime();
    facebookPixelService.initFacebookPixel();
    const searchParams = new URLSearchParams(document.location.search);
    this.settings.callFromFloatingButton = !!searchParams.get("cta");
  };

  public initModuleSettings(): Observable<any> {
    const params = requestParamsImpl.requestParams;
    return getModuleSettings(
      params.restaurantId,
      params.bookingId,
      params.bookingToken,
      params.preview
    ).pipe(tap(this.initFromModuleSettings));
  }
}

export const moduleSettings = new ModuleSettingsStore();
